/* oxlint-disable react-hooks/exhaustive-deps -- LINTER migration follow up */
/* oxlint-disable grafit-oxlint/no-tailwind-font-classes -- Typography 2 migration */
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { useCellPersistence } from "../../hooks/use-cell-persistence";
import { cn } from "../../utils/cn";
import { DEFAULT_DEBOUNCE_DELAY, useDebouncedValue } from "../../utils/debounce-react-event";

export interface InputProps extends Omit<React.ComponentProps<"input">, "size"> {
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  name: string;
  variants?: VariantProps<typeof inputVariants>;
  value: Exclude<React.ComponentProps<"input">["value"], undefined>;
  type?: React.ComponentProps<"input">["type"] | "decimal";
  onFocus?: React.ComponentProps<"input">["onFocus"];
}

const LOW_PRESENCE_BASE =
  "grow rounded-(--low-precence-round) border-none bg-transparent ring-offset-2 focus-within:ring-1 focus-within:ring-action-600 group-focus-within/row:bg-white";

const inputVariants = cva(
  "flex w-full border border-neutral-200 bg-white text-sm ring-offset-white placeholder:text-slate-300 focus-within:outline-hidden data-[disabled]:bg-neutral-100 data-[disabled]:opacity-100 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      size: {
        table: "h-5.5 rounded-md px-0.5 py-1",
        default: "h-10 rounded-lg px-3 py-2",
        small: "h-7.5 rounded-md px-1.5 py-0.5",
        xs: "rounded-md px-1 py-0.5",
      },
      state: {
        default: "focus-within:border-action-600",
        error: "ring-2 ring-red-200 ring-offset-2 focus-within:ring-red-300",
      },
      presence: {
        default: "",
        low: `${LOW_PRESENCE_BASE} group-hover/row:bg-white`,
        /** Same as `low`, minus the hover highlight, for grids that already tint the whole row on hover. */
        lowQuiet: LOW_PRESENCE_BASE,
        embedded:
          "grow rounded-none border-0 bg-transparent ring-offset-0 focus-within:ring-0 focus-within:ring-offset-0",
      },
      type: {
        default: "",
        mono: "font-mono tabular-nums",
      },
      text: {
        default: "",
        xs: "text-xs",
      },
      ring: {
        default: "",
        none: "focus-within:ring-0",
      },
    },
    defaultVariants: {
      size: "default",
      state: "default",
      presence: "default",
      type: "default",
      text: "default",
      ring: "default",
    },
  },
);

const Input = ({
  ref,
  startContent,
  type,
  endContent,
  className,
  name,
  variants,
  onFocus,
  autoComplete = "off",
  ...props
}: InputProps) => {
  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (["number", "decimal"].includes(type ?? "text") && ["ArrowUp", "ArrowDown"].includes(e.key)) {
        e.preventDefault();
      }
      props.onKeyDown?.(e);
    },
    [type, props.onKeyDown],
  );

  const isDisabled = props.disabled ?? false;

  return (
    <div data-disabled={isDisabled ? "true" : undefined} className={cn(inputVariants({ ...variants, className }))}>
      {!!startContent && <span className="pointer-events-none flex items-center text-neutral-500">{startContent}</span>}
      <input
        onFocus={onFocus}
        className={cn(
          "w-full bg-transparent tabular-nums outline-hidden [appearance:textfield] file:border-0 file:bg-transparent file:font-medium file:text-slate-950 file:text-sm focus-visible:outline-hidden [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          {
            "ps-1.5": !!startContent,
            "pe-1": !!endContent,
          },
          { "text-end": type === "number" || type === "decimal" },
        )}
        ref={ref}
        name={name}
        type={type === "decimal" ? "text" : type}
        onKeyDown={onKeyDown}
        autoComplete={autoComplete}
        {...(autoComplete === "off"
          ? {
              "data-lpignore": "true", //lastpass
              "data-bitwarden-ignore": "true", //bitwarden
              "data-form-type": "other", //dashlane
            }
          : {})}
        {...props}
      />
      {!!endContent && (
        <span className="pointer-events-none flex items-center whitespace-nowrap text-neutral-500">{endContent}</span>
      )}
    </div>
  );
};

const InputNumber = ({ className, ...props }: InputProps) => {
  return (
    <Input
      className={cn(
        "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
        className,
      )}
      {...props}
      type="number"
      onWheel={(e) => e.currentTarget.blur()}
    />
  );
};

export interface DebouncedInputProps extends Omit<InputProps, "defaultValue" | "onChange" | "value"> {
  onChange: NonNullable<InputProps["onChange"]>;
  value: string | number;
  noNullValue?: boolean;
}

const DebouncedInput = React.memo(
  ({ onChange, onBlur, onKeyDown, ref, value, noNullValue, ...props }: DebouncedInputProps) => {
    const {
      value: inputValue,
      handleChange,
      handleBlur,
      internalFlushPendingChange,
    } = useDebouncedValue({ initialValue: value, onChange, onBlur, delay: DEFAULT_DEBOUNCE_DELAY });

    // Enter submits the surrounding form, which never blurs: commit now or the pending change dies with the unmount.
    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
          internalFlushPendingChange();
        }
        onKeyDown?.(event);
      },
      [internalFlushPendingChange, onKeyDown],
    );

    return (
      <Input
        {...props}
        value={props.type === "number" && noNullValue && inputValue === "" ? "0" : inputValue}
        ref={ref}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    );
  },
);

DebouncedInput.displayName = "DebouncedInput";

/** Cell-editing input: edits stay local while typing, commit once on blur, Escape reverts. */
const CellInput = ({ onChange, onBlur, onKeyDown, ref, value, ...props }: Omit<DebouncedInputProps, "noNullValue">) => {
  const {
    value: inputValue,
    handleChange,
    handleBlur,
    handleKeyDown,
  } = useCellPersistence<HTMLInputElement>({ initialValue: value, onChange, onBlur, onKeyDown });

  return (
    <Input
      {...props}
      value={inputValue}
      ref={ref}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
};

const INPUT_VARIANTS = Object.freeze({
  SIZE_XS: { size: "xs" as const },
  SIZE_DEFAULT: { size: "default" as const },
  SIZE_SMALL: { size: "small" as const },
  LOW_PRESENCE: { size: "xs" as const, presence: "low" as const },
  TYPE_MONO: { type: "mono" as const },
  TEXT_XS: { text: "xs" as const },
  TABLE: {
    size: "table" as const,
    presence: "low" as const,
    text: "xs" as const,
    ring: "none" as const,
  },
  TABLE_QUIET: {
    size: "table" as const,
    presence: "lowQuiet" as const,
    text: "xs" as const,
    ring: "none" as const,
  },
});

Input.VARIANTS = INPUT_VARIANTS;

export { CellInput, DebouncedInput, Input, InputNumber };
